import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import { Alert, Spinner } from "reactstrap";
import constants from "../Constants";
import PartnerAccessRequestForm from "./PartnerAccessRequestForm";
import "./PartnerAccessRequestForm.css";

function formatSubmittedAt(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function statusMeta(state) {
  switch (state) {
    case "active":
      return {
        color: "success",
        icon: "mdi-check-circle",
        title: "Partner API access is active",
        lead: "Create and manage sandbox or production keys in Integrations.",
      };
    case "pending_review":
      return {
        color: "info",
        icon: "mdi-clock-outline",
        title: "Application under review",
        lead: "We received your request and will email you when access is active.",
      };
    case "suspended":
      return {
        color: "warning",
        icon: "mdi-shield-alert",
        title: "Partner API access suspended",
        lead: "Contact QikTruck support before submitting a new application.",
      };
    default:
      return null;
  }
}

/**
 * Loads Partner API access + application status, then shows the apply form only when appropriate.
 */
class PartnerAccessPanel extends Component {
  state = {
    loading: true,
    access: null,
    loadError: "",
  };

  componentDidMount() {
    this.refresh();
  }

  auth() {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: cookie.load("session-id-qt"),
      },
    };
  }

  refresh = () => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) {
      this.setState({ loading: false, access: null });
      return;
    }

    this.setState({ loading: true, loadError: "" });
    axios
      .get(`${constants.baseURL}/partner/keys/access`, this.auth())
      .then((res) => {
        this.setState({ loading: false, access: res.data || null });
        if (this.props.onAccessLoaded) this.props.onAccessLoaded(res.data || null);
      })
      .catch(() => {
        this.setState({
          loading: false,
          loadError: "Could not load your Partner API status. Refresh the page or try again shortly.",
        });
      });
  };

  handleSuccess = (email) => {
    if (this.props.onSuccess) this.props.onSuccess(email);
    this.refresh();
  };

  renderStatusCard() {
    const appStatus = this.state.access?.application_status;
    const meta = statusMeta(appStatus?.state);
    if (!meta) return null;

    const latest = appStatus?.latest_application;
    const submitted = formatSubmittedAt(latest?.submitted_at);

    return (
      <div className={`qt-partner-app-status qt-partner-app-status--${appStatus.state}`}>
        <Alert color={meta.color} className="mb-0">
          <div className="qt-partner-app-status__head">
            <i className={"mdi " + meta.icon + " qt-partner-app-status__icon"} aria-hidden />
            <div>
              <div className="fw-semibold">{meta.title}</div>
              <div className="small mb-0">{meta.lead}</div>
            </div>
          </div>

          {latest ? (
            <dl className="qt-partner-app-status__facts small mb-0">
              {latest.company_legal_name ? (
                <>
                  <dt>Company</dt>
                  <dd>{latest.company_legal_name}</dd>
                </>
              ) : null}
              {latest.email ? (
                <>
                  <dt>Contact</dt>
                  <dd>{latest.email}</dd>
                </>
              ) : null}
              {submitted ? (
                <>
                  <dt>Submitted</dt>
                  <dd>{submitted}</dd>
                </>
              ) : null}
              {latest.id ? (
                <>
                  <dt>Reference</dt>
                  <dd>
                    <code>{latest.id}</code>
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}

          {appStatus.state === "active" ? (
            <div className="qt-partner-app-status__actions">
              <a href="/integrations?type=partner#keys" className="btn btn-sm btn-success">
                <i className="mdi mdi-key me-1" />
                Manage API keys
              </a>
              <a href="/integrations?type=partner" className="btn btn-sm btn-outline-secondary">
                Open Integrations
              </a>
            </div>
          ) : null}

          {appStatus.state === "pending_review" ? (
            <p className="small text-muted mb-0 mt-3">
              Need to change company details? Email{" "}
              <a href="mailto:support@qiktruck.co.za?subject=Partner%20API%20application%20update">
                support@qiktruck.co.za
              </a>{" "}
              with your reference instead of submitting again.
            </p>
          ) : null}

          {appStatus.state === "suspended" ? (
            <p className="small mb-0 mt-3">
              <a href="mailto:support@qiktruck.co.za?subject=Partner%20API%20access%20restored">
                Contact support@qiktruck.co.za
              </a>
            </p>
          ) : null}
        </Alert>
      </div>
    );
  }

  render() {
    const { knownDomains, compact, requireLogin, sourceRef } = this.props;
    const { loading, access, loadError } = this.state;
    const sessionId = cookie.load("session-id-qt");

    if (requireLogin && !sessionId) {
      return (
        <PartnerAccessRequestForm
          sourceRef={sourceRef}
          knownDomains={knownDomains}
          compact={compact}
          requireLogin
        />
      );
    }

    if (loading) {
      return (
        <div className="qt-partner-app-status-loading text-center py-4">
          <Spinner size="sm" color="primary" className="me-2" />
          Checking your Partner API status…
        </div>
      );
    }

    if (loadError) {
      return (
        <Alert color="warning" className="mb-3">
          {loadError}
        </Alert>
      );
    }

    const appStatus = access?.application_status;
    const showForm = appStatus?.can_submit_application !== false;

    return (
      <>
        {this.renderStatusCard()}
        {showForm ? (
          <PartnerAccessRequestForm
            sourceRef={sourceRef}
            knownDomains={knownDomains}
            compact={compact}
            requireLogin={requireLogin}
            onSuccess={this.handleSuccess}
          />
        ) : null}
      </>
    );
  }
}

export default PartnerAccessPanel;
